/**
 * §H Visual Identity AI Suggest — request builders, draft guards, and
 * coverage contract. Posts through requestModelFieldSuggestions
 * (POST /brand-bible/ai-suggest, section H). Never uses aiSuggestService.
 *
 * Three buttons, ceiling of three, covering every editable §H field:
 *   visual-assets       → logo_refs, photo_library_ref   (grounded)
 *   visual-styling      → color_tokens, font_names       (grounded)
 *   visual-badge-notes  → badge_usage_notes              (creative draft)
 * real_photos_only is derived/read-only — explicit non-suggest.
 */
import {
  isValidUnverifiedDraft,
  requestModelFieldSuggestions,
  type AISuggestRequest,
  type AISuggestResponse,
  type ContextItem,
  type FieldDraft,
  type FieldRefusal,
  type TargetFieldMapping,
} from '@/services/brandBibleAiSuggest';
import type { ColorTokenRole, LogoRef } from '@/features/brand-bible-wizard/types';

export const SECTION_H_CODE = 'H' as const;

export const H_SECTION_IDS = {
  assets: 'visual-assets',
  styling: 'visual-styling',
  badge: 'visual-badge-notes',
} as const;

export const SECTION_H_EDITABLE_FIELDS = [
  'logo_refs',
  'photo_library_ref',
  'color_tokens',
  'font_names',
  'badge_usage_notes',
] as const;

export const SECTION_H_EXCLUDED_FIELDS = ['real_photos_only'] as const;

export const SECTION_H_SUGGESTION_GROUPS = [
  { sectionId: H_SECTION_IDS.assets, fields: ['logo_refs', 'photo_library_ref'] as const },
  { sectionId: H_SECTION_IDS.styling, fields: ['color_tokens', 'font_names'] as const },
  { sectionId: H_SECTION_IDS.badge, fields: ['badge_usage_notes'] as const },
] as const;

export const SUGGEST_SOURCE = 'unverified — review each';

export const PROVIDER_ERROR_MSG =
  "Suggestions couldn't be generated — the AI service didn't respond. Nothing was changed; try again.";
export const OUTPUT_ERROR_MSG =
  "The AI returned a response that didn't fit the expected shape, so it was discarded — nothing was written. Try again.";
export const MISS_MSG =
  "One suggestion couldn't be matched to a field, so it was dropped — nothing was written. Try again.";
export const MISSING_TENANT_MSG = 'tenant_id is required for Brand Bible AI suggestions';

export const ASSETS_POINTER_NOTE =
  "Logo paths and photo-library pointers aren't suggested — they have to come from this client's own assets. Enter them yourself.";
export const REAL_PHOTOS_NO_SUGGEST =
  "Real photos only isn't suggested — it follows the image source and isn't editable here.";

export const COLOR_ROLES: { role: ColorTokenRole; label: string }[] = [
  { role: 'primary', label: 'Primary' },
  { role: 'secondary', label: 'Secondary' },
  { role: 'accent', label: 'Accent' },
  { role: 'background', label: 'Background' },
  { role: 'text', label: 'Text' },
  { role: 'additional', label: 'Additional' },
];

const HEX_RE = /#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/;
const OFFICIAL_WORDING = /\b(official|approved|verified|public use)\b/i;
const CANNED_SOURCE = /demo data|backend pending|guided default/i;

const INSTRUCTIONS: Record<(typeof SECTION_H_EDITABLE_FIELDS)[number], string> = {
  logo_refs:
    'Return a logo path, URL, or filename ONLY if that exact pointer already appears in section_context. Never invent a path or filename. Never call it official, detected, or approved. If none is recorded, refuse.',
  photo_library_ref:
    'Return a photo-library pointer ONLY if that exact value already appears in section_context. Never invent a path. Never call it official or detected. If none is recorded, refuse.',
  color_tokens:
    'Return a hex colour ONLY if that exact hex already appears in section_context. You may repeat a recorded hex for an empty role. Never invent a hex. Never call it official, detected, or approved. If none is recorded, refuse.',
  font_names:
    'Return a typeface name ONLY if that exact name already appears in section_context. Never invent a typeface. Never call it official or detected. If none is recorded, refuse.',
  badge_usage_notes:
    'Propose short unverified draft wording for how badges, seals, or watermarks may be used. Do not state any policy as established fact. Do not use the words official, approved, verified, or public use. Operator must edit. One or two short sentences. No trade, industry, product, price, or rule names.',
};

export function hasOfficialOrVerifiedWording(text: string): boolean {
  return OFFICIAL_WORDING.test(text);
}

export function isValidHexToken(value: string): boolean {
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

export function extractRecordedHex(value: string, recordedHexes: string[]): string | null {
  const match = value.match(HEX_RE);
  if (!match) return null;
  const found = recordedHexes.find((hex) => hex.toLowerCase() === match[0].toLowerCase());
  return found ?? null;
}

export type HSuggestItem = {
  label: string;
  target_field: (typeof SECTION_H_EDITABLE_FIELDS)[number];
  value: string;
};

const bound = (xs: string[]): string[] =>
  xs
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => x.slice(0, 400))
    .slice(0, 20);

const boundContext = (items: ContextItem[]): ContextItem[] =>
  items
    .map((item) => ({ key: item.key, value: item.value.trim().slice(0, 1500) }))
    .filter((item) => item.key && item.value);

function target(
  field: (typeof SECTION_H_EDITABLE_FIELDS)[number],
  opts: { kind: 'chips' | 'text'; claim_policy: 'creative' | 'grounded'; existing: string[]; dismissed: string[]; max_drafts: number },
): TargetFieldMapping {
  return {
    output_key: field,
    target_field: field,
    instruction: INSTRUCTIONS[field],
    kind: opts.kind,
    max_drafts: opts.max_drafts,
    claim_policy: opts.claim_policy,
    existing: bound(opts.existing),
    dismissed: bound(opts.dismissed),
  };
}

export function recordedLogoPointers(logoRefs: LogoRef[]): string[] {
  return logoRefs.map((row) => (row.reference ?? '').trim()).filter(Boolean);
}

export function recordedHexes(colorTokens: Partial<Record<ColorTokenRole, string>>): string[] {
  return COLOR_ROLES.map(({ role }) => (colorTokens[role] ?? '').trim()).filter(isValidHexToken);
}

export function emptyColorRoles(colorTokens: Partial<Record<ColorTokenRole, string>>): ColorTokenRole[] {
  return COLOR_ROLES.filter(({ role }) => !isValidHexToken(colorTokens[role] ?? '')).map(({ role }) => role);
}

export function buildAssetsRequest(input: {
  logoRefs: LogoRef[];
  photoLibraryRef: string;
  dismissed: string[];
}): AISuggestRequest {
  const pointers = recordedLogoPointers(input.logoRefs);
  const photo = (input.photoLibraryRef ?? '').trim();
  const section_context = boundContext([
    ...input.logoRefs.flatMap((row, i) => {
      const items: ContextItem[] = [];
      if (row.kind) items.push({ key: `logo_refs.${i}.kind`, value: row.kind });
      const reference = (row.reference ?? '').trim();
      if (reference) items.push({ key: `logo_refs.${i}.reference`, value: reference });
      return items;
    }),
    ...(photo ? [{ key: 'photo_library_ref', value: photo }] : []),
  ]);

  return {
    section: SECTION_H_CODE,
    section_id: H_SECTION_IDS.assets,
    prompt: 'Draft only from supplied section_context. Do not invent logo paths, filenames, or photo-library pointers.',
    targets: [
      target('logo_refs', {
        kind: 'chips',
        claim_policy: 'grounded',
        existing: pointers,
        dismissed: input.dismissed,
        max_drafts: 3,
      }),
      target('photo_library_ref', {
        kind: 'chips',
        claim_policy: 'grounded',
        existing: photo ? [photo] : [],
        dismissed: input.dismissed,
        max_drafts: 1,
      }),
    ],
    tenant_context: [],
    section_context,
  };
}

export function buildStylingRequest(input: {
  colorTokens: Partial<Record<ColorTokenRole, string>>;
  fontNames: string[];
  dismissed: string[];
}): AISuggestRequest {
  const hexes = recordedHexes(input.colorTokens);
  const fonts = input.fontNames.map((n) => n.trim()).filter(Boolean);
  const allRolesFilled = emptyColorRoles(input.colorTokens).length === 0;
  const section_context = boundContext([
    ...COLOR_ROLES.flatMap(({ role }) => {
      const value = (input.colorTokens[role] ?? '').trim();
      return isValidHexToken(value) ? [{ key: `color_tokens.${role}`, value }] : [];
    }),
    ...(fonts.length ? [{ key: 'font_names', value: fonts.join(', ') }] : []),
  ]);

  return {
    section: SECTION_H_CODE,
    section_id: H_SECTION_IDS.styling,
    prompt: 'Draft only from supplied section_context. Do not invent hex colours or typefaces. Never call them official or approved.',
    targets: [
      target('color_tokens', {
        kind: 'chips',
        claim_policy: 'grounded',
        // Leave existing empty while any role is unset so a recorded hex can be
        // repeated for an empty role. Once every role is set, exclude them.
        existing: allRolesFilled ? hexes : [],
        dismissed: input.dismissed,
        max_drafts: 3,
      }),
      target('font_names', {
        kind: 'chips',
        claim_policy: 'grounded',
        existing: fonts,
        dismissed: input.dismissed,
        max_drafts: 3,
      }),
    ],
    tenant_context: [],
    section_context,
  };
}

export function buildBadgeRequest(input: {
  logoKinds: string[];
  realPhotosOnly: boolean | null;
  existingNotes: string;
  dismissed: string[];
}): AISuggestRequest {
  const kinds = Array.from(new Set(input.logoKinds.map((k) => (k ?? '').trim()).filter(Boolean)));
  const section_context = boundContext([
    ...(kinds.length ? [{ key: 'logo_kinds', value: kinds.join(', ') }] : []),
    ...(input.realPhotosOnly === null
      ? []
      : [{ key: 'real_photos_only', value: input.realPhotosOnly ? 'true' : 'false' }]),
  ]);
  const existing = input.existingNotes.trim() ? [input.existingNotes.trim()] : [];

  return {
    section: SECTION_H_CODE,
    section_id: H_SECTION_IDS.badge,
    prompt:
      'Propose short, plain draft wording an operator can edit for badge, seal, and watermark usage. Do not state any policy as established fact. Do not interpolate a business name or description.',
    targets: [
      target('badge_usage_notes', {
        kind: 'text',
        claim_policy: 'creative',
        existing,
        dismissed: input.dismissed,
        max_drafts: 2,
      }),
    ],
    tenant_context: [],
    section_context,
  };
}

function mappedDraft(draft: FieldDraft): FieldDraft | null {
  if (!isValidUnverifiedDraft(draft)) return null;
  if (draft.output_key !== draft.target_field) return null;
  if (hasOfficialOrVerifiedWording(draft.value)) return null;
  const text = draft.value.trim();
  if (!text) return null;
  return draft;
}

export function itemFromAssetsDraft(
  draft: FieldDraft,
  recorded: { refs: string[]; photo: string },
): HSuggestItem | null {
  const ok = mappedDraft(draft);
  if (!ok) return null;
  const value = ok.value.trim();
  const refsLc = new Set(recorded.refs.map((r) => r.toLowerCase()));
  if (ok.target_field === 'logo_refs') {
    if (!refsLc.has(value.toLowerCase())) return null;
    const canonical = recorded.refs.find((r) => r.toLowerCase() === value.toLowerCase()) ?? value;
    return { label: `Logo — ${canonical}`, target_field: 'logo_refs', value: canonical };
  }
  if (ok.target_field === 'photo_library_ref') {
    if (!recorded.photo || recorded.photo.toLowerCase() !== value.toLowerCase()) return null;
    return { label: `Photo library — ${recorded.photo}`, target_field: 'photo_library_ref', value: recorded.photo };
  }
  return null;
}

export function itemFromStylingDraft(
  draft: FieldDraft,
  recorded: { hexes: string[]; fonts: string[]; emptyRoles: ColorTokenRole[] },
): HSuggestItem | null {
  const ok = mappedDraft(draft);
  if (!ok) return null;
  const value = ok.value.trim();
  if (ok.target_field === 'color_tokens') {
    if (recorded.emptyRoles.length === 0) return null;
    const hex = extractRecordedHex(value, recorded.hexes);
    if (!hex) return null;
    const nextRole = recorded.emptyRoles[0];
    const roleLabel = COLOR_ROLES.find((r) => r.role === nextRole)?.label ?? nextRole;
    return {
      label: `Colour — reuse ${hex} (already on this record) for the empty ${roleLabel} role`,
      target_field: 'color_tokens',
      value: hex,
    };
  }
  if (ok.target_field === 'font_names') {
    const canonical = recorded.fonts.find((f) => f.toLowerCase() === value.toLowerCase());
    if (!canonical) return null;
    return { label: `Fonts — ${canonical}`, target_field: 'font_names', value: canonical };
  }
  return null;
}

export function itemFromBadgeDraft(draft: FieldDraft): HSuggestItem | null {
  const ok = mappedDraft(draft);
  if (!ok || ok.target_field !== 'badge_usage_notes') return null;
  return { label: ok.value.trim(), target_field: 'badge_usage_notes', value: ok.value.trim() };
}

export function classifySuggestError(err: unknown): 'provider' | 'output' | 'tenant' {
  const message = err instanceof Error ? err.message : String(err);
  if (/tenant_id is required/i.test(message)) return 'tenant';
  const detail = String((err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail ?? '');
  if (detail.toLowerCase().includes('unmapped') || /missing drafts or refusals/i.test(message)) return 'output';
  return 'provider';
}

export function provesModelProvenance(res: AISuggestResponse): boolean {
  return Boolean(res.model || res.provider || res.provenance?.model || res.provenance?.provider);
}

export function isCannedSuggestSource(source: string | undefined): boolean {
  return Boolean(source && CANNED_SOURCE.test(source));
}

export async function fetchHSuggestions(
  request: AISuggestRequest,
  tenantId: string | null,
  fetchFieldSuggestions: typeof requestModelFieldSuggestions = requestModelFieldSuggestions,
): Promise<AISuggestResponse> {
  const data = await fetchFieldSuggestions(request, tenantId);
  if (!provesModelProvenance(data) || isCannedSuggestSource(data.source)) {
    throw new Error('The AI model returned an invalid or unmapped response.');
  }
  return data;
}

export function refusalsForRequest(request: AISuggestRequest, reason: string): FieldRefusal[] {
  return request.targets.map((t) => ({
    output_key: t.output_key,
    target_field: t.target_field,
    reason,
    unsafe_categories: [],
  }));
}
